import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { instructorsApi } from '../services/api';

interface Instructor {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  avatar?: string;
  role: 'INSTRUCTOR';
  isActive: boolean;
  createdAt: string;
  courses?: {
    id: string;
    title: string;
    studentsCount: number;
  }[];
}

export const InstructorsPage: React.FC = () => {
  const [instructors, setInstructors] = useState<Instructor[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingInstructor, setEditingInstructor] = useState<Instructor | null>(null);

  // New instructor form data
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    isActive: true
  });

  useEffect(() => {
    fetchInstructors();
  }, []);

  const fetchInstructors = async () => {
    try {
      setLoading(true);
      const response = await instructorsApi.getAll({
        search: searchTerm,
        status: statusFilter === 'ALL' ? 'all' : statusFilter.toLowerCase(),
      });
      
      if (response.data.success) {
        // La API de instructores devuelve { instructors: [...], pagination: {...} }
        const instructorsData = response.data.data.instructors || response.data.data.users || response.data.data || [];
        setInstructors(Array.isArray(instructorsData) ? instructorsData : []);
      } else {
        console.error('API returned error:', response.data.message);
        setInstructors([]);
      }
    } catch (error) {
      console.error('Error fetching instructors:', error);
      alert('Error al cargar instructores. Revisa la conexión con el servidor.');
      setInstructors([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateInstructor = async () => {
    try {
      if (!formData.firstName || !formData.lastName || !formData.email || !formData.password) {
        alert('Por favor completa todos los campos requeridos');
        return;
      }

      await instructorsApi.create(formData);
      
      // Reset form
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        isActive: true
      });
      
      setShowCreateModal(false);
      await fetchInstructors();
      alert('Instructor creado exitosamente');
    } catch (error: any) {
      console.error('Error creating instructor:', error);
      const message = error?.response?.data?.message || 'Error al crear instructor';
      alert(message);
    }
  };

  const handleUpdateInstructor = async () => {
    if (!editingInstructor) return;

    try {
      await instructorsApi.update(editingInstructor.id, {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        isActive: formData.isActive
      });
      
      setEditingInstructor(null);
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        isActive: true
      });
      
      await fetchInstructors();
      alert('Instructor actualizado exitosamente');
    } catch (error: any) {
      console.error('Error updating instructor:', error);
      const message = error?.response?.data?.message || 'Error al actualizar instructor';
      alert(message);
    }
  };

  const handleToggleStatus = async (instructorId: string, isActive: boolean) => {
    try {
      await instructorsApi.updateStatus(instructorId, !isActive);
      await fetchInstructors();
    } catch (error) {
      console.error('Error updating instructor status:', error);
      alert('Error al actualizar el estado del instructor');
    }
  };

  const handleDeleteInstructor = async (instructorId: string) => {
    if (!confirm('¿Estás seguro de que quieres eliminar este instructor? Esta acción no se puede deshacer.')) {
      return;
    }

    try {
      await instructorsApi.delete(instructorId);
      await fetchInstructors();
      alert('Instructor eliminado exitosamente');
    } catch (error: any) {
      console.error('Error deleting instructor:', error);
      const message = error?.response?.data?.message || 'Error al eliminar instructor';
      alert(message);
    }
  };

  const openEditModal = (instructor: Instructor) => {
    setEditingInstructor(instructor);
    setFormData({
      firstName: instructor.firstName,
      lastName: instructor.lastName,
      email: instructor.email,
      password: '',
      isActive: instructor.isActive
    });
  };

  const closeModal = () => {
    setShowCreateModal(false);
    setEditingInstructor(null);
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      isActive: true
    });
  };

  console.log('DEBUG: instructors type:', typeof instructors, 'isArray:', Array.isArray(instructors), 'value:', instructors);
  const filteredInstructors = (Array.isArray(instructors) ? instructors : []).filter((instructor) => {
    const matchesSearch = 
      instructor.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      instructor.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      instructor.email.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = 
      statusFilter === 'ALL' || 
      (statusFilter === 'ACTIVE' && instructor.isActive) ||
      (statusFilter === 'INACTIVE' && !instructor.isActive);

    return matchesSearch && matchesStatus;
  });

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestión de Instructores</h1>
          <p className="text-gray-600">Administra todos los instructores de la plataforma</p>
        </div>
        <Button 
          onClick={() => setShowCreateModal(true)}
          className="bg-blue-600 hover:bg-blue-700"
        >
          + Nuevo Instructor
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="p-6">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Instructores</p>
              <p className="text-2xl font-bold text-gray-900">{instructors.length}</p>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Activos</p>
              <p className="text-2xl font-bold text-gray-900">
                {instructors.filter(i => i.isActive).length}
              </p>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center">
            <div className="p-2 bg-orange-100 rounded-lg">
              <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Cursos Creados</p>
              <p className="text-2xl font-bold text-gray-900">
                {instructors.reduce((sum, i) => sum + (i.courses?.length || 0), 0)}
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* Filters */}
      <Card className="p-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <Input
              type="text"
              placeholder="Buscar instructores por nombre o email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full"
            />
          </div>
          <div className="sm:w-48">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="ALL">Todos los estados</option>
              <option value="ACTIVE">Activos</option>
              <option value="INACTIVE">Inactivos</option>
            </select>
          </div>
        </div>
      </Card>

      {/* Instructors Table */}
      <Card>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Instructor
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Cursos
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Estado
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Registro
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {(filteredInstructors || []).map((instructor) => (
                <tr key={instructor.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <div className="h-10 w-10 rounded-full bg-purple-500 flex items-center justify-center">
                          <span className="text-white font-medium">
                            {instructor.firstName[0]}{instructor.lastName[0]}
                          </span>
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {instructor.firstName} {instructor.lastName}
                        </div>
                        <div className="text-sm text-gray-500">{instructor.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {instructor.courses?.length || 0} cursos
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      instructor.isActive 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {instructor.isActive ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(instructor.createdAt)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => openEditModal(instructor)}
                      >
                        Editar
                      </Button>
                      <Button
                        variant={instructor.isActive ? "secondary" : "primary"}
                        size="sm"
                        onClick={() => handleToggleStatus(instructor.id, instructor.isActive)}
                      >
                        {instructor.isActive ? 'Desactivar' : 'Activar'}
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => handleDeleteInstructor(instructor.id)}
                        className="text-red-600 hover:text-red-800"
                      >
                        Eliminar
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredInstructors.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500">No se encontraron instructores</p>
          </div>
        )}
      </Card>

      {/* Create/Edit Instructor Modal */}
      {(showCreateModal || editingInstructor) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-900">
                {editingInstructor ? 'Editar Instructor' : 'Nuevo Instructor'}
              </h2>
              <button
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-500"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre *
                </label>
                <Input
                  type="text"
                  value={formData.firstName}
                  onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
                  placeholder="Nombre del instructor"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Apellido *
                </label>
                <Input
                  type="text"
                  value={formData.lastName}
                  onChange={(e) => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
                  placeholder="Apellido del instructor"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email *
                </label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="email@ejemplo.com"
                />
              </div>

              {!editingInstructor && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Contraseña *
                  </label>
                  <Input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                    placeholder="Contraseña temporal"
                  />
                </div>
              )}

              <div className="flex items-center">
                <input
                  id="isActive"
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="isActive" className="ml-2 block text-sm text-gray-900">
                  Instructor activo
                </label>
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <Button variant="secondary" onClick={closeModal}>
                Cancelar
              </Button>
              <Button 
                onClick={editingInstructor ? handleUpdateInstructor : handleCreateInstructor}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {editingInstructor ? 'Actualizar' : 'Crear'} Instructor
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};